// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Deal } from '@interfaces/deal.interfaces';
import { Assignees, DdjDocument, DealDocuments, DocumentStatus, FormDocumentUploadType, InternalDocumentTypeIds } from '@interfaces/document.interfaces';
import { DocActions, User, UserTypes } from '@interfaces/user.interfaces';
import { Vehicle } from '@interfaces/vehicle.interfaces';
import { DocumentsService } from '@services/documents.service';
import { UserService } from '@services/user.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { catchError, takeWhile } from 'rxjs/operators';
import { FinalDocModalComponent } from 'src/app/modals/final-doc-modal/final-doc-modal.component';
import { isIdentification } from 'src/app/utils/document-utils';
import { isMobileOrTablet } from 'src/app/utils/utils';
import { MobileOnlyUploadModalComponent } from 'src/app/modals/mobile-only-upload-modal/mobile-only-upload-modal.component';
import { VendorService } from '@services/vendor.service';
import { MultiSelectModalComponentProps } from '@interfaces/interfaces';
import { MultiSelectModalComponent } from 'src/app/modals/multi-select-modal/multi-select-modal.component';
import { ModalSize } from '@interfaces/shared.interfaces';
import { SharedService } from '@services/shared.service';
import { DownloadService } from '@services/download.service';
import { ErrorModalComponent } from 'src/app/modals/error-modal/error-modal.component';
import { EventsService } from '@services/events.service';

@Component({
    selector: 'buyer-deal',
    templateUrl: './buyer-deal.component.html',
    styleUrls: ['./buyer-deal.component.scss'],
})
export class BuyerDealComponent implements OnInit, AfterViewInit, OnDestroy {
    private isComponentAlive = true;
    public deal: Deal;
    public user: User;
    public vehicle: Vehicle;
    public assignee: Assignees;
    public buyerEsignRequired = false;
    public coBuyerEsignRequired = false;
    public docList: DdjDocument[] = [];
    public dealDocs: DealDocuments;
    public finalizeDocumentsEnabled = false;

    constructor(
        private readonly deviceService: DeviceDetectorService,
        private readonly dialog: MatDialog,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly documentsService: DocumentsService,
        private readonly eventService: EventsService,
        private readonly userService: UserService,
        private readonly vendorService: VendorService,
        private readonly sharedService: SharedService,
        private readonly downloadService: DownloadService
    ) {}

    ngOnInit(): void {
        const data = this.route.snapshot.data;
        this.deal = data.deal;
        this.user = data.user;
        this.vehicle = data.deal.vehicle;
        this.dealDocs = data.dealDocs;
        this.assignee = this.deal?.coBuyer?.userId === this.user.userId ? Assignees.cobuyer : Assignees.buyer;
        this.finalizeDocumentsEnabled = this.documentsService.finalizeEnabledForCustomer(this.assignee);

        if (this.deal && this.deal.dealId) {
            this.userService.setUserCurrentDealer(this.deal.dealerId);

            if (this.dealDocs) {
                this.docList = this.getRelevantDocs();
            } else {
                this.loadDocuments();
            }
        }
    }

    ngAfterViewInit(): void {
        this.isComponentAlive = true;

        this.eventService.build().then(async () => {
            await this.eventService.subscribeToDeal(this.deal.dealId);
            this.eventService.onDocumentQueueUpdate((update) => {
                console.log(`Document ${update.documentId} modified by an external session (another user, or browser).  Updating our data...`);
                if (this.dealDocs) {
                    this.docList = this.getRelevantDocs();
                } else {
                    this.loadDocuments();
                }
            });
        });
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
        if (this.deal.dealId) {
            this.eventService.unsubscribeFromDeal(this.deal.dealId);
        }
        this.eventService.offDocumentQueueUpdate();
    }

    public viewPrivacy(): void {
        window.open('/agreements/PrivacyPolicy', '_blank');
    }

    public viewTandC(): void {
        window.open('/agreements/EndUserAgreement', '_blank');
    }

    downloadDocuments(): void {
        const availableItems = [].concat(this.dealDocs?.buyer || [], this.dealDocs?.complete || []);
        const data: MultiSelectModalComponentProps = {
            // A document is considered available if:
            // - There is a fileName on the document
            // - The document is not of type Impact or Laser
            //     - If the document is marked as readyForSignatures we are good to display no matter the type
            availableItems: availableItems.filter(
                (d) =>
                    d.fileName &&
                    ((d.uploadTypeId !== FormDocumentUploadType.Print_From_Impact_Form && d.uploadTypeId !== FormDocumentUploadType.Print_From_PDF) ||
                        d.readyForSignatures)
            ),
            searchKey: 'displayName',
            returnedKey: 'documentId',
            title: 'Download Documents',
            description: 'Please select the documents you wish to download.',
            btnText: 'Download Documents',
            search: 'Search for a document using any term in the name',
            selectAll: true,
        };

        this.dialog
            .open(MultiSelectModalComponent, {
                width: this.sharedService.getModalWidth(ModalSize.Medium) + 'px',
                closeOnNavigation: true,
                panelClass: 'add-multiple-dialog',
                data,
            })
            .afterClosed()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((result) => {
                if (result?.selectedItems) {
                    this.documentsService
                        .downloadDocs$(this.deal.dealId, false, result?.selectedItems)
                        .pipe(
                            takeWhile(() => this.isComponentAlive),
                            catchError((err) => {
                                console.error('error downloading: ', err);
                                throw err;
                            })
                        )
                        .subscribe(async (response) => {
                            const links = response.data;
                            const urls = [];
                            links.docs.forEach((doc) => {
                                urls.push(doc.filename);
                            });

                            for (let i = 0; i < urls.length; i++) {
                                await this.downloadService.downloadFile(urls[i]);
                            }
                        });
                }
            });
    }

    loadDocuments(): void {
        this.documentsService
            .getDocsList$(this.deal.dealId)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe(() => {
                this.docList = this.getRelevantDocs();
            });
    }

    getRelevantDocs(): DdjDocument[] {
        return this.getCustomerPendingDocs(this.deal.buyer.userId === this.user.userId ? UserTypes.Buyer : UserTypes.CoBuyer);
    }

    getCompletedDocs(): DdjDocument[] {
        return this.documentsService.getCompleteDocs();
    }

    hasCustomerDocsForDownload(): boolean {
        return this.dealDocs.buyer.length > 0 || this.dealDocs.cobuyer.length > 0 || this.dealDocs.complete.length > 0;
    }

    getCustomerPendingDocs(customerType: UserTypes.Buyer | UserTypes.CoBuyer): DdjDocument[] {
        const docs = customerType === UserTypes.Buyer ? this.documentsService.getCustomerPending() : this.documentsService.getCoBuyerPending();

        // dealer can select buyer or cobuyer esign consent for the esign consent form so we much check for both
        const esignRequired = docs.some(
            (doc) =>
                doc.internalDocumentTypeId === InternalDocumentTypeIds.BuyerESignConsent ||
                doc.internalDocumentTypeId === InternalDocumentTypeIds.CobuyerESignConsent
        );

        if (customerType === UserTypes.Buyer) {
            this.buyerEsignRequired = esignRequired;
        } else {
            this.coBuyerEsignRequired = esignRequired;
        }

        // Sort so that esign consent is first
        return docs.sort((a, b) =>
            a.internalDocumentTypeId === InternalDocumentTypeIds.BuyerESignConsent || a.internalDocumentTypeId === InternalDocumentTypeIds.CobuyerESignConsent
                ? -1
                : a.displayName.localeCompare(b.displayName)
        );
    }

    isDocumentCursorDisabled(document: DdjDocument) {
        if (
            (document.internalDocumentTypeId === InternalDocumentTypeIds.BuyerFinalSignatureDocument ||
                document.internalDocumentTypeId === InternalDocumentTypeIds.CoBuyerFinalSignatureDocument) &&
            !this.finalizeDocumentsEnabled
        ) {
            return true;
        } else {
            return false;
        }
    }

    isDocumentDisabled(document: DdjDocument) {
        if (
            (this.buyerEsignRequired || this.coBuyerEsignRequired) &&
            document.internalDocumentTypeId !== InternalDocumentTypeIds.BuyerESignConsent &&
            document.internalDocumentTypeId !== InternalDocumentTypeIds.CobuyerESignConsent
        ) {
            return true;
        }
        if (
            (document.internalDocumentTypeId === InternalDocumentTypeIds.BuyerFinalSignatureDocument ||
                document.internalDocumentTypeId === InternalDocumentTypeIds.CoBuyerFinalSignatureDocument) &&
            !this.finalizeDocumentsEnabled
        ) {
            return true;
        }
        if (isIdentification(document.internalDocumentTypeId) && !isMobileOrTablet()) {
            return true;
        }
        return false;
    }

    public reloadPage(): void {
        location.reload();
    }

    routeTo(document: DdjDocument, type: string = null): void {
        if (isIdentification(document.internalDocumentTypeId) && !isMobileOrTablet() && type !== DocActions.view) {
            this.showMobileOnlyUploadModal(document.internalDocumentTypeId);
            return;
        }
        if (document.isDealerOnly && (this.user.userType === UserTypes.Buyer || this.user.userType === UserTypes.CoBuyer)) {
            this.dialog.open(ErrorModalComponent, {
                width: this.deviceService.isDesktop() ? '700px' : '100vw',
                data: {
                    title: 'Unable to View Document',
                    message: 'This document is only accessible by a dealer. Please contact your dealer for help with this document',
                },
            });
            return;
        }
        if (
            document.documentStatusId !== DocumentStatus.Complete &&
            (this.deal.buyer.userId === this.user.userId || this.deal.coBuyer.userId === this.user.userId) &&
            (this.buyerEsignRequired || this.coBuyerEsignRequired) &&
            document.internalDocumentTypeId !== InternalDocumentTypeIds.BuyerESignConsent &&
            document.internalDocumentTypeId !== InternalDocumentTypeIds.CobuyerESignConsent
        ) {
            return;
        } else if (
            !this.finalizeDocumentsEnabled &&
            (document.internalDocumentTypeId === InternalDocumentTypeIds.BuyerFinalSignatureDocument ||
                document.internalDocumentTypeId === InternalDocumentTypeIds.CoBuyerFinalSignatureDocument)
        ) {
            //Figure out buyer or cobuyer by assignee access

            this.dialog.open(FinalDocModalComponent, {
                width: this.deviceService.isDesktop() ? '700px' : '100vw',
                closeOnNavigation: true,
                data: {
                    dealerPending: this.documentsService.getDealerPending().filter((x) => !this.isDocumentTypeFinalDoc(x.internalDocumentTypeId)).length,
                    buyerPending: this.documentsService.getCustomerPending().filter((x) => !this.isDocumentTypeFinalDoc(x.internalDocumentTypeId)).length,
                    cobuyerPending: this.documentsService.getCoBuyerPending().filter((x) => !this.isDocumentTypeFinalDoc(x.internalDocumentTypeId)).length,
                    documentName: document.displayName,
                },
            });
            return;
        }

        if (type === DocActions.view) {
            this.router.navigate(['main', 'vehicles', document.dealId, document.documentId, 'view']);
        } else if (document?.fileName || this.vendorService.isDocumentForRouteOne(document)) {
            this.router.navigate(['/main', 'vehicles', document.dealId, document.documentId, 'sign']);
        } else {
            const url = isIdentification(document.internalDocumentTypeId) && isMobileOrTablet() ? 'uploadIdn' : 'upload';
            this.router.navigate(['/main', 'vehicles', document.dealId, document.documentId, url]);
        }
    }

    private showMobileOnlyUploadModal(internalDocumentTypeId: InternalDocumentTypeIds) {
        this.dialog.open(MobileOnlyUploadModalComponent, {
            width: '700px',
            closeOnNavigation: true,
            data: {
                internalDocumentTypeId: internalDocumentTypeId,
            },
        });
    }

    isDocumentTypeFinalDoc(docTypeId?: InternalDocumentTypeIds): boolean {
        return docTypeId === InternalDocumentTypeIds.BuyerFinalSignatureDocument || docTypeId === InternalDocumentTypeIds.CoBuyerFinalSignatureDocument;
    }
}
